import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { copiesAPI, saleListAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { ArrowLeft, Copy, Check, RefreshCw, Link2, Facebook, Search, Sun, Moon, Tag, Crown, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Currency symbols for display
const CURRENCY_SYMBOLS = {
  DKK: 'kr', SEK: 'kr', NOK: 'kr',
  EUR: '€', USD: '$', GBP: '£', CHF: 'CHF',
  CAD: 'CA$', AUD: 'A$', NZD: 'NZ$',
  JPY: '¥', CNY: '¥', KRW: '₩',
  PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
  BRL: 'R$', MXN: 'MX$', INR: '₹',
};

// Format price from cents to full currency
const formatPrice = (cents, currency = 'DKK') => {
  if (!cents) return '-';
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const amount = (cents / 100).toLocaleString('da-DK');
  // Put symbol after amount for Nordic currencies, before for others
  if (['DKK', 'SEK', 'NOK'].includes(currency)) {
    return `${amount} ${symbol}`;
  }
  return `${symbol}${amount}`;
};

export default function Sales() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [saleList, setSaleList] = useState(null);
  const [userCurrency, setUserCurrency] = useState('DKK');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [copied, setCopied] = useState(false);
  const [copiedFb, setCopiedFb] = useState(false);
  const [showRotateDialog, setShowRotateDialog] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [updatingActive, setUpdatingActive] = useState(false);
  
  const isPro = user?.plan === 'PRO' || user?.plan === 'LIFETIME_PRO';

  const fetchData = useCallback(async () => {
    if (!isPro) {
      setLoading(false);
      return;
    }
    
    try {
      const [itemsRes, listRes] = await Promise.all([
        copiesAPI.getForSale(),
        saleListAPI.ensure()
      ]);
      setItems(itemsRes.data.copies || []);
      setSaleList(listRes.data);
      setUserCurrency(listRes.data.user_currency || itemsRes.data.user_currency || 'DKK');
    } catch (err) {
      console.error('Error fetching sales data:', err);
      toast.error('Could not load sales data');
    } finally {
      setLoading(false);
    }
  }, [isPro]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopyLink = async () => {
    if (!saleList?.public_url) return;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(saleList.public_url);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = saleList.public_url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopied(true);
      toast.success('Link kopieret!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Final fallback - show link in prompt
      window.prompt('Copy this link:', saleList.public_url);
    }
  };

  const handleCopyFacebookText = async () => {
    if (!saleList?.public_url) return;
    
    const text = `I'm selling these vinyl records (price per item). DM for interest.
Link: ${saleList.public_url}

First come, first served. Can be shipped or picked up.`;
    
    try {
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-HTTPS contexts
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        textArea.remove();
      }
      setCopiedFb(true);
      toast.success('Facebook-tekst kopieret!');
      setTimeout(() => setCopiedFb(false), 2000);
    } catch (err) {
      // Final fallback - show text in prompt
      window.prompt('Copy this text:', text);
    }
  };

  const handleToggleActive = async (checked) => {
    setUpdatingActive(true);
    try {
      const res = await saleListAPI.update({ is_active: checked });
      setSaleList(res.data);
      toast.success(checked ? 'Link activated' : 'Link deactivated');
    } catch (err) {
      toast.error('Could not update status');
    } finally {
      setUpdatingActive(false);
    }
  };

  const handleRotateToken = async () => {
    setRotating(true);
    try {
      const res = await saleListAPI.rotateToken();
      setSaleList(prev => ({ ...prev, public_token: res.data.public_token, public_url: res.data.public_url }));
      setShowRotateDialog(false);
      toast.success('New link generated. Old links no longer work.');
    } catch (err) {
      toast.error('Could not generate new link');
    } finally {
      setRotating(false);
    }
  };

  // Filter and sort items
  const filteredItems = items
    .filter(item => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        item.artist?.toLowerCase().includes(query) ||
        item.title?.toLowerCase().includes(query)
      );
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price_low':
          return (a.sale_price || 0) - (b.sale_price || 0);
        case 'price_high':
          return (b.sale_price || 0) - (a.sale_price || 0);
        case 'artist':
          return (a.artist || '').localeCompare(b.artist || '', 'da');
        case 'newest':
        default:
          return 0; // Already sorted by API
      }
    });

  // Non-PRO upsell screen
  if (!isPro) {
    return (
      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-40 glass">
          <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button variant="ghost" onClick={() => navigate('/')} className="gap-2" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
            </div>
          </div>
        </header>
        
        <main className="max-w-[600px] mx-auto px-4 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center">
            <Crown className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Sale List is a PRO feature</h1>
          <p className="text-muted-foreground mb-8">
            Upgrade to PRO to mark records for sale and share a public sale list with buyers.
          </p>
          <Button onClick={() => navigate('/upgrade')} className="gap-2">
            <Crown className="w-4 h-4" />
            Upgrade to PRO
          </Button>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="gap-2" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="w-9 h-9"
                data-testid="theme-toggle-btn"
              >
                {theme === 'dark' ? (
                  <Sun className="w-4 h-4 text-primary" />
                ) : (
                  <Moon className="w-4 h-4 text-primary" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
            <Tag className="w-8 h-8 text-primary" />
            For Sale
          </h1>
          <p className="text-muted-foreground">
            Share your public link on Facebook. Transactions happen outside Vinyllog.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Share Controls */}
            <Card className="mb-6 bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-primary" />
                  Your public link
                </CardTitle>
                <CardDescription>
                  {items.length} {items.length === 1 ? 'record' : 'records'} for sale
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Link display */}
                <div className="flex items-center gap-2">
                  <Input
                    value={saleList?.public_url || ''}
                    readOnly
                    className="bg-secondary font-mono text-sm"
                    data-testid="public-url-input"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={handleCopyLink}
                    disabled={!saleList?.public_url}
                    data-testid="copy-link-btn"
                  >
                    {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={handleCopyFacebookText}
                    className="gap-2"
                    data-testid="copy-facebook-btn"
                  >
                    {copiedFb ? <Check className="w-4 h-4 text-green-500" /> : <Facebook className="w-4 h-4" />}
                    Copy Facebook text
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.open(saleList?.public_url, '_blank')}
                    disabled={!saleList?.public_url}
                    className="gap-2"
                    data-testid="preview-link-btn"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Preview
                  </Button>
                </div>
                
                {/* Link controls */}
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={saleList?.is_active ?? true}
                      onCheckedChange={handleToggleActive}
                      disabled={updatingActive}
                      data-testid="link-active-switch"
                    />
                    <span className="text-sm">
                      Link active
                      {!saleList?.is_active && <span className="text-muted-foreground ml-2">(deactivated)</span>}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRotateDialog(true)}
                    className="gap-2 text-muted-foreground"
                    data-testid="rotate-token-btn"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate link
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Empty state */}
            {items.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-16 text-center">
                  <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">No records marked for sale</h3>
                  <p className="text-muted-foreground mb-6">
                    Go to a record in your collection and mark it &quot;For sale&quot; to add it here.
                  </p>
                  <Button onClick={() => navigate('/')} variant="outline">
                    Go to collection
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Search and sort */}
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search artist or album..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-secondary"
                      data-testid="search-input"
                    />
                  </div>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px] bg-secondary" data-testid="sort-select">
                      <SelectValue placeholder="Sort" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest first</SelectItem>
                      <SelectItem value="price_low">Price (low-high)</SelectItem>
                      <SelectItem value="price_high">Price (high-low)</SelectItem>
                      <SelectItem value="artist">Artist</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Items grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-testid="sales-grid">
                  {filteredItems.map((item) => (
                    <div
                      key={item.copy_id}
                      className="bg-card border border-border rounded-lg overflow-hidden hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/release/${item.discogs_release_id}`)}
                      data-testid={`sale-item-${item.copy_id}`}
                    >
                      {/* Cover image */}
                      <div className="aspect-square bg-secondary">
                        {item.cover_image ? (
                          <img
                            src={item.cover_image}
                            alt={`${item.artist} - ${item.title}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <img src="/logo-icon.svg" alt="" className="w-12 h-12 opacity-30" />
                          </div>
                        )}
                      </div>
                      
                      {/* Info */}
                      <div className="p-3">
                        <h3 className="font-medium text-sm line-clamp-1">{item.title}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-1">{item.artist}</p>
                        
                        {/* Grades */}
                        <div className="flex gap-1 mt-2">
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            M: {item.condition_vinyl}
                          </Badge>
                          <Badge variant="outline" className="text-xs px-1.5 py-0">
                            S: {item.condition_cover}
                          </Badge>
                        </div>
                        
                        {/* Price */}
                        <div className="mt-2 text-lg font-bold text-primary">
                          {formatPrice(item.sale_price, item.sale_currency || userCurrency)}
                        </div>
                        
                        {/* Note */}
                        {item.sale_note && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2 italic">
                            &quot;{item.sale_note}&quot;
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

      {/* Rotate Token Confirmation Dialog */}
      <AlertDialog open={showRotateDialog} onOpenChange={setShowRotateDialog}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate link?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new link and invalidate the old one. Anyone with the old link will no longer be able to see your sale list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-rotate-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRotateToken}
              disabled={rotating}
              data-testid="confirm-rotate-btn"
            >
              {rotating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
